import { Vector2, Vector3 } from '@math.gl/core';
import proj4 from 'proj4';

export type NorthingEasting = {
    n: number;
    e: number;
    z: number;
};

// Proj4 string for web mercator
const epsg3857proj4 = '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs';

function wgs84ToProjectCoordinate(latlon: ILatLonElevation): NorthingEasting {
    const projectCoord = proj4(kuura.getProjectProj4Text()).forward({
        x: latlon.lon,
        y: latlon.lat,
        z: latlon.elevation,
    });

    return { n: projectCoord.x, e: projectCoord.y, z: latlon.elevation };
}

function projectCoordinateToWgs84(projectCoord: NorthingEasting): ILatLonElevation {
    const latLonVector = proj4(kuura.getProjectProj4Text()).inverse({
        x: projectCoord.e,
        y: projectCoord.n,
        z: projectCoord.z,
    });
    return { lat: latLonVector.y, lon: latLonVector.x, elevation: projectCoord.z };
}

const webMercatorToWgs84 = (position: NorthingEasting): ILatLonElevation => {
    const latLonArray: number[] = proj4(epsg3857proj4).inverse([position.e, position.n]);
    return { lat: latLonArray[1], lon: latLonArray[0], elevation: position.z };
};

const wgs84ToWebMercator = (position: ILatLonElevation): NorthingEasting => {
    const mercatorArray: number[] = proj4(epsg3857proj4).forward([position.lon, position.lat]);
    return { n: mercatorArray[1], e: mercatorArray[0], z: position.elevation };
};

/**
 * Check if a point is to the left of a line defined by two points
 * @param c1 First point of the line
 * @param c2 Second point of the line
 * @param point Point to test
 * @returns true if point is to the left of the line
 */
function isLeft(c1: Vector3, c2: Vector3, point: Vector3): boolean {
    const e1 = c1.x;
    const n1 = c1.y;
    const e2 = c2.x;
    const n2 = c2.y;
    const e0 = point.x;
    const n0 = point.y;

    return (e2 - e1) * (n0 - n1) - (n2 - n1) * (e0 - e1) > 0;
}

const _v3a = new Vector3();
const _v3b = new Vector3();
const _v3c = new Vector3();

/**
 * Project coordinates to a cross-section point
 * @param coords The coordinates to project (as Vector3 where x=easting, y=northing, z=elevation)
 * @param lineStart The start point of the cross section line as Vector3
 * @param lineEnd The end point of the cross section line as Vector3
 * @returns CrossSectionPoint with distance from center and elevation
 */
function worldCoordToCrossSectionCoord(coords: Vector3, lineStart: Vector3, lineEnd: Vector3): Vector2 {
    // Calculate 2D distance from center point
    const centerPoint = _v3c.set((lineStart.x + lineEnd.x) / 2, (lineStart.y + lineEnd.y) / 2, 0);
    const dx = coords.x - centerPoint.x;
    const dy = coords.y - centerPoint.y;
    let distanceFromCenter = Math.sqrt(dx * dx + dy * dy);

    // Calculate normal of the line
    const lineDir = _v3a.set(lineEnd.x - lineStart.x, lineEnd.y - lineStart.y, 0);
    const lineNormal = _v3b.set(-lineDir.y, lineDir.x, 0).normalize();

    const alignmentStart = centerPoint.clone().add(lineNormal.scale(1000));
    const alignmentEnd = centerPoint.clone().add(lineNormal.scale(-1000));

    // Check if point is to the left of the alignment line
    if (isLeft(alignmentStart, alignmentEnd, coords)) {
        distanceFromCenter = -distanceFromCenter;
    }

    return new Vector2(-distanceFromCenter, coords.z);
}

function calculateMercatorDistortionFromLatitude(latitude: number): number {
    const latitudeInRadians: number = latitude * (Math.PI / 180);
    const distortionCorrection: number = 1 / Math.cos(latitudeInRadians);

    return distortionCorrection;
}

export const CoordsUtils = {
    wgs84ToProjectCoordinate,
    projectCoordinateToWgs84,
    webMercatorToWgs84,
    wgs84ToWebMercator,
    worldCoordToCrossSectionCoord,
    calculateMercatorDistortionFromLatitude,
};
